// PWA-обвязка: регистрация service worker, применение обновления, установка.
// Всё, что связано с браузерными PWA-API, живёт здесь — компоненты работают
// только со стором.

/** Событие установки; в типах TS его нет, поэтому описываем сами. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: InstallPromptEvent | null = null
let waiting: ServiceWorker | null = null

/** Панель уже запущена как приложение (с домашнего экрана)? */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS до сих пор ходит своим путём
    (navigator as { standalone?: boolean }).standalone === true
  )
}

/** iOS не показывает beforeinstallprompt — там установка только вручную. */
export function isIos(): boolean {
  const ua = navigator.userAgent
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS притворяется макинтошем, отличается только тачем
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  )
}

/**
 * Зарегистрировать service worker и следить за появлением новой версии.
 *
 * `onUpdate` зовётся, когда новая версия скачана и ждёт активации, — но только
 * если страница уже управляется воркером. При самой первой установке ждать
 * нечего, и звать нечего.
 */
export function registerServiceWorker(onUpdate: () => void): void {
  if (!('serviceWorker' in navigator)) return
  // В dev sw.js не собирается; регистрировать нечего, а закешированная
  // оболочка мешала бы горячей перезагрузке.
  if (!import.meta.env.PROD) return

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      // updateViaCache: 'none' — сам sw.js всегда перепроверяется на сервере,
      // иначе обновление панели зависит от милости HTTP-кеша
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        const track = (worker: ServiceWorker | null) => {
          if (!worker) return
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              waiting = worker
              onUpdate()
            }
          })
        }
        if (registration.waiting && navigator.serviceWorker.controller) {
          waiting = registration.waiting
          onUpdate()
        }
        registration.addEventListener('updatefound', () => track(registration.installing))
        // Возврат на вкладку — хороший момент проверить, не вышла ли новая версия
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void registration.update()
        })
      })
      .catch(() => {
        // без service worker панель работает как обычный сайт — это не авария
      })
  })
}

/** Применить скачанное обновление: активировать воркер и перезагрузить вкладку. */
export function applyUpdate(): void {
  const worker = waiting
  if (!worker) {
    location.reload()
    return
  }
  // controllerchange придёт после skipWaiting — тогда и перезагружаемся,
  // иначе страницу подхватит ещё старый воркер.
  let reloaded = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return
    reloaded = true
    location.reload()
  })
  worker.postMessage('skip-waiting')
}

// ---------- цвет системной панели (M10.1) ----------
//
// В установленном приложении браузер красит строку состояния и «шапку» окна в
// theme-color из <meta>. Тема панели переключается на ходу, значит и мета-тег
// обязан переключаться — иначе в светлой теме сверху остаётся тёмная полоса.
//
// Значение берём не из своей таблицы цветов, а из уже посчитанного токена
// `--bg`: вторая копия палитры разъезжается с tokens.css на первой же правке.

/** Канва — единственный доступный конвертер цветов: присвоенный fillStyle
 *  возвращается нормализованным (#rrggbb или rgb(...)). Нужен потому, что
 *  токены записаны в oklch, а theme-color в старых Android-браузерах понимает
 *  только простые формы. */
let probe: CanvasRenderingContext2D | null | undefined

function toPlainColor(value: string): string | null {
  if (probe === undefined) probe = document.createElement('canvas').getContext('2d')
  if (!probe) return null
  // Заведомо известное значение до присваивания: если браузер не разобрал
  // цвет, fillStyle остаётся прежним — только так «не понял» и отличается от
  // «понял». Чёрного в палитре нет, ложного срабатывания не будет.
  probe.fillStyle = '#000000'
  probe.fillStyle = value
  const out = probe.fillStyle
  return typeof out === 'string' && out !== '#000000' ? out : null
}

function syncThemeColor(): boolean {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim()
  if (!raw) return false
  const color = toPlainColor(raw)
  if (!color) return false
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  if (meta.content !== color) meta.content = color
  return true
}

/**
 * Держать theme-color в согласии с активной темой.
 *
 * Слушаем атрибут `data-theme` на <html>, а не стор: тему ставит appStore, но
 * поставить её может кто угодно ещё (восстановление из localStorage, будущий
 * системный режим), и мета-тег не должен от этого зависеть.
 */
export function watchThemeColor(): () => void {
  if (!syncThemeColor()) {
    // Токены ещё не приехали (в dev стили подключает сам бандл) — повторим,
    // когда страница догрузится.
    window.addEventListener('load', () => void syncThemeColor(), { once: true })
  }
  const observer = new MutationObserver(() => void syncThemeColor())
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  })
  return () => observer.disconnect()
}

// Запускаем сами, на импорте модуля: цвет системной панели — свойство всего
// приложения, отдельного места, откуда его завести, в оболочке нет.
if (typeof document !== 'undefined') watchThemeColor()

/** Начать слушать предложение установки. Возвращает отписку. */
export function watchInstallPrompt(onChange: (canInstall: boolean) => void): () => void {
  const onPrompt = (event: Event) => {
    // без preventDefault Chrome покажет свою мини-плашку и съест событие
    event.preventDefault()
    deferredPrompt = event as InstallPromptEvent
    onChange(true)
  }
  const onInstalled = () => {
    deferredPrompt = null
    onChange(false)
  }
  window.addEventListener('beforeinstallprompt', onPrompt)
  window.addEventListener('appinstalled', onInstalled)
  return () => {
    window.removeEventListener('beforeinstallprompt', onPrompt)
    window.removeEventListener('appinstalled', onInstalled)
  }
}

/** Показать системный диалог установки. Одноразовый: событие больше не придёт. */
export async function promptInstall(): Promise<boolean> {
  const event = deferredPrompt
  if (!event) return false
  deferredPrompt = null
  await event.prompt()
  const choice = await event.userChoice
  return choice.outcome === 'accepted'
}
